
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge"; 
import { CameraIcon, UserCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export function ProfileForm() {
  const { user, checkUserStatus } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    gender: "",
    height: "",
    weight: "",
    dietaryRestrictions: [] as string[],
    emailNotifications: true,
    pushNotifications: true,
    reminderTime: "morning",
    dataSharing: false,
    goals: [] as string[],
    healthConcerns: "",
    
    // Additional fields to match onboarding data
    fitnessLevel: "",
    activityFrequency: "",
    dietType: "",
    mealsPerDay: "",
    waterIntake: "",
    preferredExercises: [] as string[],
    averageSleep: "",
    sleepQuality: "",
    sleepIssues: [] as string[],
    currentSupplements: "",
    supplementGoals: "",
    stressLevel: "",
    mindfulnessPractices: [] as string[],
    mentalHealthConcerns: "",
    primaryGoals: [] as string[],
    secondaryGoals: [] as string[],
    timeframe: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (error) {
          toast({
            title: "Error",
            description: "Failed to load profile data: " + error.message,
            variant: "destructive"
          });
          console.error("Error fetching profile:", error);
          setLoading(false);
          return;
        }
        
        console.log("Fetched profile data:", data);
        
        if (data) {
          // Ensure all array fields are properly handled
          const goalsArray = Array.isArray(data.goals) ? data.goals : 
                            (data.goals ? [data.goals] : []);
          
          const dietaryRestrictions = Array.isArray(data.dietary_restrictions) ? 
                                    data.dietary_restrictions : [];
          
          const preferredExercises = Array.isArray(data.preferred_exercises) ? 
                                   data.preferred_exercises : [];
          
          const sleepIssues = Array.isArray(data.sleep_issues) ? 
                            data.sleep_issues : [];
          
          const supplements = Array.isArray(data.supplements) ? 
                            data.supplements : [];
          
          const mindfulnessPractices = Array.isArray(data.mindfulness_practices) ? 
                                     data.mindfulness_practices : [];
          
          const wellnessGoals = Array.isArray(data.wellness_goals) ? 
                              data.wellness_goals : [];
          
          // Format height from database format (e.g. "5'11"") to a display format
          let formattedHeight = data.height || "";
                            
          setProfile({
            name: data.name || "",
            email: data.email || "",
            phone: data.phone || "",
            age: data.age || "",
            gender: data.gender || "",
            height: formattedHeight,
            weight: data.weight || "",
            dietaryRestrictions: dietaryRestrictions,
            emailNotifications: data.email_notifications ?? true,
            pushNotifications: data.push_notifications ?? true,
            reminderTime: data.reminder_time || "morning",
            dataSharing: data.data_sharing ?? false,
            goals: goalsArray,
            healthConcerns: data.health_concerns || "",
            
            // Additional fields from onboarding
            fitnessLevel: data.fitness_level || "",
            activityFrequency: data.activity_level || "",
            dietType: data.food_preferences && data.food_preferences.length > 0 ? data.food_preferences[0] : "",
            mealsPerDay: data.meals_per_day || "",
            waterIntake: data.water_intake || "",
            preferredExercises: preferredExercises,
            averageSleep: data.sleep_hours || "",
            sleepQuality: data.sleep_quality || "",
            sleepIssues: sleepIssues,
            currentSupplements: supplements.length > 0 ? supplements[0] : "",
            supplementGoals: data.supplement_goals || "",
            stressLevel: data.stress_level || "",
            mindfulnessPractices: mindfulnessPractices,
            mentalHealthConcerns: data.health_concerns || "",
            primaryGoals: wellnessGoals,
            secondaryGoals: [],
            timeframe: data.timeframe || "",
          });
        }
      } catch (err) {
        console.error("Error in profile fetch:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [user, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleSwitchChange = (name: string, checked: boolean) => {
    setProfile((prev) => ({ ...prev, [name]: checked }));
  };
  
  const handleRadioChange = (name: string, value: string) => {
    setProfile((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    setLoading(true);
    
    try {
      // Process goals - ensure it's an array before sending to Supabase
      let processedGoals: string[] = [];
      
      if (typeof profile.goals === 'string') {
        // If it's a string, split by commas or newlines to create an array
        processedGoals = (profile.goals as string)
          .split(/[,\n]/)
          .map(goal => goal.trim())
          .filter(goal => goal.length > 0);
      } else if (Array.isArray(profile.goals)) {
        // If already an array, just use it
        processedGoals = profile.goals.map(g => String(g));
      }
      
      // Convert dietType to food_preferences array
      const foodPreferences = profile.dietType ? [profile.dietType] : [];
      
      // Convert currentSupplements to supplements array
      const supplements = profile.currentSupplements ? [profile.currentSupplements] : [];
      
      // Map activity frequency to a valid enum value for database
      let activityLevel = "low"; // Default value
      
      if (profile.activityFrequency === "0") activityLevel = "low";
      else if (profile.activityFrequency === "1-2") activityLevel = "moderate";
      else if (profile.activityFrequency === "3-4") activityLevel = "high";
      else if (profile.activityFrequency === "5+") activityLevel = "very-high";
      else activityLevel = profile.activityFrequency || "low";
      
      console.log("Saving profile with data:", {
        user_id: user.id,
        name: profile.name,
        email: profile.email,
        height: profile.height,
        activity_level: activityLevel,
        processed_goals: processedGoals,
        food_preferences: foodPreferences,
        supplements: supplements
      });
      
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          age: profile.age,
          gender: profile.gender,
          height: profile.height,
          weight: profile.weight,
          dietary_restrictions: profile.dietaryRestrictions,
          email_notifications: profile.emailNotifications,
          push_notifications: profile.pushNotifications,
          reminder_time: profile.reminderTime,
          data_sharing: profile.dataSharing,
          goals: processedGoals,
          health_concerns: profile.healthConcerns,
          
          // Additional fields from onboarding
          fitness_level: profile.fitnessLevel,
          activity_level: activityLevel,
          food_preferences: foodPreferences,
          meals_per_day: profile.mealsPerDay,
          water_intake: profile.waterIntake,
          preferred_exercises: profile.preferredExercises,
          sleep_hours: profile.averageSleep,
          sleep_quality: profile.sleepQuality,
          sleep_issues: profile.sleepIssues,
          supplements: supplements,
          supplement_goals: profile.supplementGoals,
          stress_level: profile.stressLevel,
          mindfulness_practices: profile.mindfulnessPractices,
          wellness_goals: profile.primaryGoals,
          timeframe: profile.timeframe,
        })
        .select();

      if (error) {
        console.error("Error in upsert operation:", error);
        throw error;
      }
      
      console.log("Profile update successful, response:", data);
      
      // Refresh user profile data in the auth context
      await checkUserStatus();
      
      toast({
        title: "Success",
        description: "Your profile has been updated.",
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile: " + error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Fix for goals array/string handling - properly handling string or array type
  const getGoalsValue = () => {
    if (Array.isArray(profile.goals)) {
      return profile.goals.join('\n');
    } else if (typeof profile.goals === 'string') {
      return profile.goals as string;
    }
    return '';
  };
  
  // Helper function to render goal badges safely
  const renderGoalBadges = () => {
    if (Array.isArray(profile.goals)) {
      return profile.goals.map((goal, index) => (
        <Badge key={index} variant="outline" className="px-2 py-1">
          {goal}
        </Badge>
      ));
    } else if (typeof profile.goals === 'string') {
      return (profile.goals as string).split(',').map((goal, index) => (
        <Badge key={index} variant="outline" className="px-2 py-1">
          {goal.trim()}
        </Badge>
      ));
    }
    return null;
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full mb-8">
          <TabsTrigger value="personal">Personal Info</TabsTrigger>
          <TabsTrigger value="wellness">Wellness</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="goals">Goals & Health</TabsTrigger>
        </TabsList>
        
        <form onSubmit={handleSubmit}>
          
          <TabsContent value="personal" className="space-y-8 animate-fade-in-up">
            <div className="glass-card p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
                <div className="relative group">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src="" />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      <UserCircle className="h-12 w-12" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute inset-0 bg-black/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="ghost" size="icon" className="text-white h-8 w-8">
                      <CameraIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <h2 className="text-2xl font-medium">{profile.name}</h2>
                  <p className="text-muted-foreground">{profile.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    name="name" 
                    value={profile.name} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    name="email" 
                    type="email" 
                    value={profile.email} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input 
                    id="phone" 
                    name="phone" 
                    value={profile.phone} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input 
                    id="age" 
                    name="age" 
                    type="number" 
                    value={profile.age} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <RadioGroup 
                    value={profile.gender} 
                    onValueChange={(value) => handleRadioChange("gender", value)}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="male" id="male" />
                      <Label htmlFor="male">Male</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="female" id="female" />
                      <Label htmlFor="female">Female</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="other" id="other" />
                      <Label htmlFor="other">Other</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="height">Height</Label>
                  <Input 
                    id="height" 
                    name="height" 
                    value={profile.height} 
                    onChange={handleInputChange} 
                  />
                  <p className="text-xs text-muted-foreground">Example: 5'11"</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (lbs)</Label>
                  <Input 
                    id="weight" 
                    name="weight" 
                    value={profile.weight} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="wellness" className="space-y-8 animate-fade-in-up">
            <div className="glass-card p-6">
              <h3 className="text-lg font-medium mb-6">Nutrition & Diet</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="dietType">Diet Type</Label>
                  <Input 
                    id="dietType" 
                    name="dietType" 
                    value={profile.dietType} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mealsPerDay">Meals Per Day</Label>
                  <Input 
                    id="mealsPerDay" 
                    name="mealsPerDay" 
                    value={profile.mealsPerDay} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="waterIntake">Water Intake</Label>
                  <Input 
                    id="waterIntake" 
                    name="waterIntake" 
                    value={profile.waterIntake} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>
              
              <h3 className="text-lg font-medium mt-8 mb-6">Fitness</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="fitnessLevel">Fitness Level</Label>
                  <Input 
                    id="fitnessLevel" 
                    name="fitnessLevel" 
                    value={profile.fitnessLevel} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="activityFrequency">Activity Frequency</Label>
                  <Input 
                    id="activityFrequency" 
                    name="activityFrequency" 
                    value={profile.activityFrequency} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>
              
              <h3 className="text-lg font-medium mt-8 mb-6">Sleep</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="averageSleep">Average Sleep Duration</Label>
                  <Input 
                    id="averageSleep" 
                    name="averageSleep" 
                    value={profile.averageSleep} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sleepQuality">Sleep Quality</Label>
                  <Input 
                    id="sleepQuality" 
                    name="sleepQuality" 
                    value={profile.sleepQuality} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>
              
              <h3 className="text-lg font-medium mt-8 mb-6">Supplements</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="currentSupplements">Current Supplements</Label>
                  <Input 
                    id="currentSupplements" 
                    name="currentSupplements" 
                    value={profile.currentSupplements} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="supplementGoals">Supplement Goals</Label>
                  <Input 
                    id="supplementGoals" 
                    name="supplementGoals" 
                    value={profile.supplementGoals} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="preferences" className="space-y-8 animate-fade-in-up">
            <div className="glass-card p-6">
              <h3 className="text-lg font-medium mb-6">Notifications & Privacy</h3>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email-notifications" className="text-base">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive plan updates and wellness insights via email
                    </p>
                  </div>
                  <Switch 
                    id="email-notifications" 
                    checked={profile.emailNotifications}
                    onCheckedChange={(checked) => handleSwitchChange("emailNotifications", checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="push-notifications" className="text-base">Push Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Get alerts for reminders and important updates
                    </p>
                  </div>
                  <Switch 
                    id="push-notifications" 
                    checked={profile.pushNotifications}
                    onCheckedChange={(checked) => handleSwitchChange("pushNotifications", checked)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Daily Reminder Time</Label>
                  <RadioGroup 
                    value={profile.reminderTime} 
                    onValueChange={(value) => handleRadioChange("reminderTime", value)}
                    className="grid grid-cols-3 gap-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="morning" id="morning" />
                      <Label htmlFor="morning">Morning</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="afternoon" id="afternoon" />
                      <Label htmlFor="afternoon">Afternoon</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="evening" id="evening" />
                      <Label htmlFor="evening">Evening</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="data-sharing" className="text-base">Anonymous Data Sharing</Label>
                    <p className="text-sm text-muted-foreground">
                      Help improve Vitatee's recommendations with anonymous usage data
                    </p>
                  </div>
                  <Switch 
                    id="data-sharing" 
                    checked={profile.dataSharing}
                    onCheckedChange={(checked) => handleSwitchChange("dataSharing", checked)}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="goals" className="space-y-8 animate-fade-in-up">
            <div className="glass-card p-6">
              <h3 className="text-lg font-medium mb-6">Goals & Health Information</h3>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="goals">Wellness Goals</Label>
                  <Textarea 
                    id="goals" 
                    name="goals" 
                    value={getGoalsValue()}
                    onChange={handleInputChange} 
                    className="min-h-[120px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Describe what you want to achieve with your wellness plan.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="timeframe">Goal Timeframe</Label>
                  <Input 
                    id="timeframe" 
                    name="timeframe" 
                    value={profile.timeframe} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="healthConcerns">Health Concerns</Label>
                  <Textarea 
                    id="healthConcerns" 
                    name="healthConcerns" 
                    value={profile.healthConcerns} 
                    onChange={handleInputChange} 
                    className="min-h-[120px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    List any health conditions or concerns that might impact your wellness plan.
                  </p>
                </div>
              </div>
              
              {profile?.goals && (
                <div className="space-y-2">
                  <h3 className="text-md font-semibold">Goals</h3>
                  <div className="flex flex-wrap gap-2">
                    {renderGoalBadges()}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          
          <div className="mt-8 flex justify-end">
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Tabs>
    </div>
  );
}

export default ProfileForm;
